// src/components/AmenityBookingCalendar.tsx

import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Grid2 as Grid, Container, Icon, IconButton, Tooltip, Select, MenuItem, Menu, Paper, SelectChangeEvent, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowCircleRight, Cancel, MeetingRoom, Person } from "@mui/icons-material";
import { Amenity, APICallStatus, BookingStatus, BookingType, Unit, User, UserType } from "../../Types/UnitFeatureTypes";
import { useDispatch, useSelector } from "react-redux";
import { blockedSlots, bookingHoldError, bookingHoldId, bookingHoldRequestStatus, bookingUpdateHoldRequestStatus, bookingUpdateRequestStatus, currentUnit, fetchAmenities, fetchUnit } from "../../Redux/selectors";
import { Actions } from "../../Redux/Reducer/rootReducer";
import DropdownSelection from "../CommonComponents/DropdownSelection";
import { useAuth } from "../../context/authContext";
import { useApp } from "../../context/appContext";

const AmenityBookingCalendar: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = useState<Dayjs[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>();
  const [selectedUnitId, setSelectedUnitId] = useState<number>();
  const [availableTimes, setAvailableTimes] = useState<Dayjs[]>([]);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [selectedUnitOccupant, setSelectedUnitOccupant] = useState<number>();
  const userUnitData = useSelector(currentUnit) as Unit;
  const bookingHoldIdValue = useSelector(bookingHoldId);
  const amenitiesList = useSelector(fetchAmenities);
  const unitList = useSelector(fetchUnit);
  const bookingHoldStatus = useSelector(bookingHoldRequestStatus);
  const bookingUpdateHoldStatus = useSelector(bookingUpdateHoldRequestStatus);
  const bookingHoldErrorMessage = useSelector(bookingHoldError)
  const blockedSlotsData = useSelector(blockedSlots);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const IS_MANAGER = user?.type === UserType.MANAGER;

  const isReschedule = location?.state?.reschedule;

  const { amenity } = location.state as { amenity: number;};
  const { setSnackbar } = useApp();

  useEffect(() => {
    if (location?.state && location?.state?.reschedule) {
      setSelectedDate(dayjs(location?.state?.bookingDate));
      setSelectedTime(location?.state?.bookingTime.map((time: Date) => dayjs(time)));
      setSelectedUnitId(location?.state?.unit.id);
      setSelectedUnitOccupant(location?.state?.occupant.id);
      dispatch(Actions.fetchAmenities.blockedSlotActions.request({
        amenity_id: amenity,
        start_date: dayjs(location?.state?.bookingDate)?.format('YYYY-MM-DD') ?? ''
      }));
    }
  }
  , []);

  useEffect(() => {
    if (!amenitiesList || amenitiesList?.length === 0) {
      dispatch(Actions.fetchAmenities.amenityActions.request({}));
    }
    if (amenity) {
      setSelectedAmenity(amenitiesList.find((a) => a.id === Number(amenity)) || null);
    }
  }, [amenity, amenitiesList]);

  useEffect(() => {
    if (selectedAmenity && selectedDate && blockedSlotsData.length > 0) {
      getAvailableSlots(selectedDate);
    }
  }, [blockedSlotsData]);

  useEffect(() => {
    let unitDetails = unitList.find((unit) => selectedUnitId === unit.id);
    setSelectedUnit(unitDetails);
  }, [unitList, selectedUnitId]);

  useEffect(() => {
    if (bookingHoldStatus === APICallStatus.SUCCESS) {
        navigate("/confirmBooking", { state: {
          bookingDate: selectedDate?.toDate(),
          bookingSlots: selectedTime.map((time) => time.toDate()),
          amenity: selectedAmenity,
          holdId: bookingHoldIdValue
        }});
      } else if (bookingHoldStatus === APICallStatus.FAILURE) {
        setSnackbar(true, 'Failed to book the slot. Please try again.', 'error');
      }
      dispatch(Actions.booking.resetStatusRequestAction());
  }, [bookingHoldStatus]);

  useEffect(() => {
    if (location.state && location.state.reschedule && bookingUpdateHoldStatus === APICallStatus.SUCCESS) {
        navigate("/confirmBooking", { state: {
          reschedule: true,
          bookingDate: selectedDate?.toDate(),
          bookingSlots: selectedTime.map((time) => time.toDate()),
          amenity: selectedAmenity,
          holdId: location.state.booking_id
        }});
        setSnackbar(true, 'Booking hold to reschedule', 'success');
        dispatch(Actions.booking.resetStatusRequestAction());
      } else if (bookingHoldStatus === APICallStatus.FAILURE) {
        setSnackbar(true, 'Failed to book the slot. Please try again.', 'error');
        dispatch(Actions.booking.resetStatusRequestAction());
      }
  }, [bookingUpdateHoldStatus]);
  
  const getAvailableSlots = (date: Dayjs) => {
    const startHour = Number(selectedAmenity?.start_time.split(":")[0]) ?? 9;
    const startMinute = Number(selectedAmenity?.start_time.split(":")[1]) ?? 0;
    const endHour = Number(selectedAmenity?.end_time.split(":")[0]) ?? 17;
    const endMinute = Number(selectedAmenity?.end_time.split(":")[1]) ?? 0;
    const startTime = dayjs().hour(startHour).minute(startMinute); // default start time
    const endTime = dayjs().hour(endHour).minute(endMinute); // default start time
    const slotDurationInMinutes = parseInt(selectedAmenity?.slot_duration ?? '60');
  
    let availableSlots: Dayjs[] = [];
  
    if (date.isSame(dayjs(), 'day')) {
      const currentHour = dayjs().hour();
      const currentMinute = dayjs().minute();
      const currentTime = dayjs().hour(currentHour).minute(currentMinute);
      if (currentTime.isBefore(startTime)) {
        startTime.hour(currentHour).minute(currentMinute);
      } else {
        startTime.add(slotDurationInMinutes, 'minutes');
      }
    }
    for (let time = startTime.hour() * 60 + startTime.minute(); time < endTime.hour() * 60 + endTime.minute(); time += slotDurationInMinutes) {
      const hours = Math.floor(time / 60);
      const minutes = time % 60;

      const displayTime = dayjs(date).hour(hours).minute(minutes);

      if (displayTime.isBefore(dayjs())) {
        continue;
      }
      if (blockedSlotsData && blockedSlotsData.length > 0) {
        if (!blockedSlotsData.some((slot) => displayTime.isSame(dayjs(slot), 'minute'))) {
          availableSlots.push(displayTime);
        }
      } else {
        availableSlots.push(displayTime);
      }      
    }

    if (location?.state && location?.state?.reschedule) {
      availableSlots = [...availableSlots,...selectedTime].sort((a, b) => a.diff(b));
    }
  
    setAvailableTimes(availableSlots);
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    if (location.state && location.state.reschedule && date?.isSame(dayjs(location.state.bookingDate))) {
      setSelectedTime(location.state.bookingTime.map((time: Date) => dayjs(time)));
    } else {
      setSelectedTime([]);
    }
    dispatch(Actions.fetchAmenities.blockedSlotActions.request({
      amenity_id: selectedAmenity?.id ?? -1,
      start_date: date?.format('YYYY-MM-DD') ?? ''
    }));
  
    if (date) {
      getAvailableSlots(date);
    }
  };

  const handleTimeChange = (time: Dayjs | null) => {
    if (time && !selectedTime.includes(time)) {
      setSelectedTime([...selectedTime, time]);
      if (selectedAmenity?.max_slot_booking && selectedTime.length >= selectedAmenity?.max_slot_booking) {
          setSelectedTime([
            ...selectedTime.slice(1),
            time
          ]);
      }
    } else if (time && selectedTime.includes(time)) {
      setSelectedTime(selectedTime.filter((t) => t !== time));
    }
  };


  const checkIfBookingDataChanged = () => {
    if (!location?.state || !location?.state?.reschedule) return false;
    if (!selectedDate || !selectedTime) return false;
    if (!selectedDate?.isSame(dayjs(location?.state?.bookingDate))) return false;
    if (selectedTime.length !== location?.state?.bookingTime.length) return false;
    for (let i = 0; i < selectedTime.length; i++) {
      if (!selectedTime[i].isSame(dayjs(location?.state?.bookingTime[i]))) return false;
    }

    return true;
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedTime) { 
      setSnackbar(true, 'Please select both date and time.', 'error'); 
      return;
    }

    if (location?.state?.reschedule) {
      dispatch(Actions.booking.updateBookingHoldRequestAction.request({
        bookingData: {
          id: location?.state?.booking_id,
          amenity_id: selectedAmenity?.id ?? -1,
          slots: selectedTime.map((time) => time.local().toDate()),
          booking_start: selectedTime[0].local().toDate()
        }
      }));
    } else { 
      dispatch(Actions.booking.createBookingHoldRequestAction.request({
        bookingData: {
          amenity_id: selectedAmenity?.id ?? -1,
          requesting_user_id: !IS_MANAGER ? user.id : selectedUnitOccupant ?? -1,
          requester_unit_id: !IS_MANAGER ? userUnitData.id : selectedUnitId ?? -1,
          slots: selectedTime.map((time) => time.local().toDate()),
          booking_start: selectedTime[0].local().toDate(),
          type: BookingType.SINGLE_SLOT,
          status: BookingStatus.HOLD
        }
      }));
    }
  };

  const handleUnitChange = (e: SelectChangeEvent<string>) => {
    let unitDetails = unitList.find((unit) => parseInt(e.target.value) === unit.id);
    if (!unitDetails?.owner && unitDetails?.id ) {
      dispatch(Actions.fetchUnit.unitOccupantsActions.request({ id: unitDetails.id }));
    }
    setSelectedUnitId(parseInt(e.target.value));
  };


  const rescheduleWorkflowViewOnly = () => {
    return (
      isReschedule && <Container sx={{ mt: 2, mb: 4, display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
        <Stack direction="row" spacing={2}>
          <MeetingRoom sx={{mr: 2}} />
          <Typography variant="h6" >Booked Unit :</Typography>
          <Typography color="primary" variant="h6" >{location?.state?.unit?.unit_number}</Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Person sx={{mr: 2}} />
          <Typography variant="h6" >Booked For : </Typography>
          <Typography color="primary" variant="h6" >{location?.state?.occupant?.name}</Typography>
        </Stack>
      </Container>
    )
  }

  const managerWorkflow = () => {
    return (
      <Container sx={{ mt: 2, mb: 4, display: 'flex', flexDirection: 'column'}}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Typography sx={{width: '35%'}} variant="h6" gutterBottom>
          Select Unit for the booking
        </Typography>
        <DropdownSelection
          dropDownLabel='Select Unit'
          showHeader={false}
          currentValue={selectedUnitId?.toString() ?? ''}
          selectionValues={unitList.map((unit) => ({ value: unit.id.toString(), label: unit.unit_number.toString(), color: 'default' }))}
          variant='outlined'
          handleOnChange={handleUnitChange}
        />
      </Box>
      {selectedUnitId && <Box sx={{ mt: 4, display: 'flex', flexDirection: 'row' }}>
        <Typography sx={{width: '35%'}} variant="h6" gutterBottom>
          Occupant
        </Typography>
        <DropdownSelection
          dropDownLabel='Select Occupant'
          showHeader={false}
          currentValue={selectedUnitOccupant?.toString() ?? ''}
          selectionValues={
            [
              ...(selectedUnit?.owner ? [{ value: selectedUnit.owner.id.toString(), label: `${selectedUnit.owner.name} (Owner)`, color: 'green' }] : []),
              ...(selectedUnit?.tenants?.map((tenant) => ({ value: tenant.id.toString(), label: tenant.name, color: 'default' })) ?? [])
            ]}
          variant='outlined'
          handleOnChange={(e) => setSelectedUnitOccupant(parseInt(e.target.value))}
        />
      </Box>}
    </Container>)
  };

  return (
    <Paper elevation={5} sx={{padding: 6, borderRadius: 3, mb: 2}}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', verticalAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Book {selectedAmenity?.title}
          </Typography>
          <Tooltip title="Cancel" placement="top">
            <IconButton size="large" onClick={() => navigate(-1)}>
              <Cancel sx={{'&:hover': {
                color: '#f44336'
              }}} />
            </IconButton>
          </Tooltip>
        </Box>
        {IS_MANAGER && !isReschedule && managerWorkflow()}
        {rescheduleWorkflowViewOnly()}
        <Container sx={{ minHeight: 450, display: 'flex', justifyContent: 'space-between', flexDirection: 'column'}}>
          {(!IS_MANAGER || IS_MANAGER && selectedUnitId && selectedUnitOccupant) && <DatePicker
            minDate={dayjs()}
            maxDate={dayjs().add(1, "year")}
            label="Select a Date"
            value={selectedDate}
            onChange={handleDateChange}
            shouldDisableDate={(date) => date.day() === 0 || date.day() === 6}
            slotProps={{ textField: { fullWidth: true } }}
            format="DD MMM YYYY"
          />}
          {selectedDate && selectedDate > dayjs(-1) && selectedDate < dayjs().add(1, "year") && (
            <>
          <Typography variant="h6" gutterBottom sx={{ marginTop: 2 }}>
            Available Times:
            <Typography variant="subtitle1" gutterBottom color='textSecondary'>
              Select one or more time slots for booking. Each slot is for {selectedAmenity?.slot_duration} minutes.
            </Typography>
            {selectedAmenity?.max_slot_booking && <Typography variant="subtitle2" gutterBottom color='textSecondary'>
              You can book at most {selectedAmenity?.max_slot_booking} slots.
            </Typography>}
          </Typography>
          <Grid container spacing={2}>
            {availableTimes.map((time, index) => (
              <Grid size={{xs: 4}} key={index}>
              <Button
                variant={selectedTime.includes(time) ? "contained" : "outlined"}
                onClick={() => handleTimeChange(time)}
                fullWidth
              >
                {time.format('HH:mm')}
              </Button>
              </Grid>
            ))}
          </Grid>
            </>
          )}
          {selectedDate && selectedDate > dayjs(-1) && selectedDate < dayjs().add(1, "year") && <Box sx={{ marginTop: 5, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(-1)}
              sx={{ width: '45%',
                '&:hover': { backgroundColor: '#f44336', color: '#fff'}
              }}  
            >
              <Cancel sx={{ mr: 5 }}/>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBook}
              sx={{ width: '45%' }}
              disabled={selectedTime.length === 0 || checkIfBookingDataChanged()}
            >
              <ArrowCircleRight sx={{ mr: 5 }}/>
              Continue to Book
            </Button>
          </Box>}
        </Container>
      </LocalizationProvider>
    </Paper>
  );
};

export default AmenityBookingCalendar;
